import React, { useState } from 'react';
import { Badge, Drawer, Flex, Segmented, Spin } from 'antd';
import { BellOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button } from '@bettercomp/ui-components';
import { AppThemeProvider } from 'components/AppThemeProvider';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationUnread,
  useNotificationsList,
  useNotificationsSSE,
  useUnreadCount,
  type NotificationsTab,
} from 'service/notificationsService';
import { NotificationErrorBoundary } from './NotificationErrorBoundary';
import { renderNotification } from './registry/NotificationRegistry';
import { NotificationShell } from './NotificationShell';
import cn from './NotificationsDrawer.module.scss';

export function NotificationsDrawer() {
  useNotificationsSSE();
  const [open, setOpen] = useState(false);
  const { data: unreadCount = 0 } = useUnreadCount();
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMarkAllNotificationsRead();

  return (
    <>
      <Badge size="small" count={unreadCount} data-testid="notifications-drawer-badge">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          style={{ background: 'transparent' }}
          data-testid="notifications-drawer-trigger"
        />
      </Badge>
      <AppThemeProvider>
        <Drawer
          title={
            <Flex justify="space-between" align="center">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Button size="small" type="link" loading={isMarkingAllRead} onClick={() => markAllRead()}>
                  Mark all as read
                </Button>
              )}
            </Flex>
          }
          placement="right"
          open={open}
          onClose={() => setOpen(false)}
          destroyOnHidden
          classNames={{ body: cn.body }}
          data-testid="notifications-drawer"
        >
          {/* Mounted only while the drawer is open (destroyOnHidden): the tab list
              queries exist exactly when their data is on screen, so SSE-driven
              invalidations refetch nothing for closed drawers, and gcTime: 0
              drops the caches on close — every open starts from a fresh fetch. */}
          <NotificationsDrawerContent initialTab={unreadCount > 0 ? 'unread' : 'all'} unreadCount={unreadCount} />
        </Drawer>
      </AppThemeProvider>
    </>
  );
}

type ContentProps = {
  initialTab: NotificationsTab;
  unreadCount: number;
};

function NotificationsDrawerContent({ initialTab, unreadCount }: ContentProps) {
  const [tab, setTab] = useState<NotificationsTab>(initialTab);
  const {
    data: notifications = [],
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNotificationsList(tab);
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markUnread } = useMarkNotificationUnread();
  // A failed background refetch also flips isError while cached rows are still
  // valid — keep showing them; the error panel is only for an unloadable list.
  const showError = !isLoading && isError && notifications.length === 0;

  return (
    <>
      <div className={cn.segmented}>
        <Segmented<NotificationsTab>
          block
          value={tab}
          onChange={setTab}
          options={[
            { label: `Unread (${unreadCount})`, value: 'unread' },
            { label: 'All', value: 'all' },
          ]}
        />
      </div>
      {isLoading && (
        <Flex justify="center" align="center" className={cn.loading}>
          <Spin />
        </Flex>
      )}
      {/* No manual retry: recovery is automatic — the SSE reconnect invalidates
          mounted queries, window focus refetches, and reopening the drawer
          always starts from a fresh fetch. */}
      {showError && (
        <Flex vertical align="center" justify="center" gap={8} className={cn.empty} data-testid="notifications-error-state">
          <ExclamationCircleOutlined className={cn.emptyIcon} />
          <span>Couldn&apos;t load notifications</span>
          <span>Something went wrong. We&apos;ll keep retrying automatically.</span>
        </Flex>
      )}
      {!isLoading && !isError && notifications.length === 0 && (
        <Flex vertical align="center" justify="center" gap={8} className={cn.empty}>
          <CheckCircleOutlined className={cn.emptyIcon} />
          <span>You&apos;re all caught up</span>
          <span>
            No new notifications. We&apos;ll let you know when reports, exports or reviews are ready.
          </span>
        </Flex>
      )}
      {!isLoading &&
        notifications.map((notification) => {
          const content = renderNotification(notification);
          if (!content) { return null; }
          return (
            <NotificationErrorBoundary key={notification.id}>
              <NotificationShell
                notification={notification}
                onMarkRead={markRead}
                onMarkUnread={markUnread}
                content={content}
              />
            </NotificationErrorBoundary>
          );
        })}
      {!isLoading && hasNextPage && (
        <Flex justify="center" className={cn.loadMore}>
          <Button
            type="link"
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            data-testid="notifications-load-more"
          >
            Load more
          </Button>
        </Flex>
      )}
    </>
  );
}
